import { isLandlineNumber, isLandlineNumberWithShortPrefix, isMobileNumber } from './helpers';
import { formatLandlineNumber, formatMobileNumber } from './telephone';
/**
 * Formats a city name by capitalizing the first letter of each word.
 *
 * @param {string} value - The city name to format.
 * @returns {string} The formatted city name.
 * @example
 * const formattedCity = formatCity('new york'); // Returns 'New York'
 */
export const formatCity = (value = '') => value
    .trim()
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join('-');
/**
 * Formats an email address by converting it to lowercase and trimming spaces.
 *
 * @param {string} value - The email address to format.
 * @returns {string} The formatted email address.
 * @example
 * const formattedEmail = formatEmail('Test@Example.com  '); // Returns 'test@example.com'
 */
export const formatEmail = (value = '') => value.trim().toLowerCase();
/**
 * Formats initials by converting them to uppercase, removing non-word characters, and adding dots between them.
 *
 * @param {string} value - The initials to format.
 * @returns {string} The formatted initials.
 * @example
 * const formattedInitials = formatInitials('j.d.'); // Returns 'J. D.'
 */
export const formatInitials = (value = '') => value.toUpperCase().trim().replace(/\W+/g, '').split('').join('. ') +
    (value.length > 0 ? '.' : '');
/**
 * Formats a postal code by converting it to uppercase and removing non-word characters.
 *
 * @param {string} value - The postal code to format.
 * @returns {string} The formatted postal code.
 * @example
 * const formattedPostalCode = formatPostalCode('a1!b2@c3#'); // Returns 'A1B2C3'
 */
export const formatPostalCode = (value = '') => value.toUpperCase().trim().replace(/\W+/g, '');
/**
 * Formats a surname by capitalizing the first letter of each word.
 *
 * @param {string} value - The surname to format.
 * @returns {string} The formatted surname.
 * @example
 * const formattedSurname = formatSurname('john-doe'); // Returns 'John-Doe'
 */
export const formatSurname = (value = '') => value
    .trim()
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join('-');
/**
 * Formats a surname prefix by converting it to lowercase and trimming spaces.
 *
 * @param {string} value - The surname prefix to format.
 * @returns {string} The formatted surname prefix.
 * @example
 * const formattedSurnamePrefix = formatSurnamePrefix('Van Der '); // Returns 'van der'
 */
export const formatSurnamePrefix = (value = '') => value.toLowerCase().trim();
/**
 * Formats a telephone number based on its type (mobile or landline).
 *
 * @param {string} value - The telephone number to format.
 * @returns {string} The formatted telephone number.
 * @example
 * const formattedTelephone = formatTelephone('+31 (0)6 12-34 56-78'); // Returns '06 12345678'
 */
export const formatTelephone = (value = '') => {
    let number = value.replace(/\D+/g, '');
    const numberIsMobile = isMobileNumber(number);
    const numberIsLandline = isLandlineNumber(number);
    if (numberIsMobile) {
        number = formatMobileNumber(number);
        return number;
    }
    if (numberIsLandline) {
        const numberIsLandlineWithShortPrefix = isLandlineNumberWithShortPrefix(number);
        number = formatLandlineNumber(number);
        /* prettier-ignore */
        number = `${number.slice(0, numberIsLandlineWithShortPrefix ? 3 : 4)} ${number.slice(numberIsLandlineWithShortPrefix ? 3 : 4)}`;
        return number;
    }
    return number;
};
